/**
 * Who gets the Listings module in the browser (L1-3, L2-1). The server decides
 * every read and write on its own (docs/SECURITY.md "Listings"); this only
 * hides what it would refuse.
 *
 *   module (route + sidebar)  feature:listings (or listings_work), or skips checks
 *   write controls            feature:listings_work, or skips checks
 *   broker view (L2-1)        the module without listings_work: a Broker sees
 *                             only its own listing agreements (the API scopes
 *                             them), read-only, plus "Ask the assistant"
 *   task-template settings    feature:listings_templates, or skips checks (L1-4)
 *   Team workload             feature:listings_work AND widget:team_workload, or skips
 *                             checks (the page, its tab and the dashboard card; was
 *                             admin-only until 2026-10-12, now the Brokerage Assistant)
 *
 * "Skips checks" = useSkipsPermissionChecks: Super Admin or AIG staff, the server's
 * rule (data/listings-access.ts skipsChecks). Admin follows its grants.
 */
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useSkipsPermissionChecks } from '@/hooks/useSkipsPermissionChecks';
import { useWidgetAccess } from '@/widgets/useWidgetAccess';

export function useCanWorkListings(): boolean {
  const skips = useSkipsPermissionChecks();
  const hasWork = useFeatureAccess('listings_work');
  return hasWork || skips;
}

/** The module itself: feature:listings, listings_work, or skips checks. */
export function useCanSeeListings(): boolean {
  const canWork = useCanWorkListings();
  const hasListings = useFeatureAccess('listings');
  return canWork || hasListings;
}

/** The broker's view (Phase 2): in the module, without listings_work. */
export function useIsListingsBrokerView(): boolean {
  const canSee = useCanSeeListings();
  const canWork = useCanWorkListings();
  return canSee && !canWork;
}

/** The task-template library (/settings/listing-templates): feature:listings_templates, or skips checks. */
export function useCanEditListingTemplates(): boolean {
  const skips = useSkipsPermissionChecks();
  const hasTemplates = useFeatureAccess('listings_templates');
  return hasTemplates || skips;
}

/**
 * Team workload (/listings/workload, its tab, the dashboard card): listings_work plus the
 * widget:team_workload grant, or skips checks — the API's can_team_workload (data/listings-access.ts).
 */
export function useCanSeeTeamWorkload(): boolean {
  const skips = useSkipsPermissionChecks();
  const canWork = useCanWorkListings();
  const hasWidget = useWidgetAccess('team_workload');
  return skips || (canWork && hasWidget);
}
